import React from 'react';
import { useEffect, useMemo } from 'react';
import { Navigate, Outlet, useLocation, matchPath } from 'react-router-dom';
import withFirebase from '../../Firebase/withFirebase';
import { callNativeMobileApp, useTranslator } from '@front-activerse/common-components';
import { Route } from '../types';

/* eslint-disable-next-line */
export interface PrivateRouteProps {
  title?: string;
  mobileAppTabIdentifier?: string;
  updateScene: (
    sceneId: number,
    lastPlaybackPosition: number,
    isPlaybackCompleted: boolean,
    __typename: string
  ) => any;
  trackFirebaseEvent: any;
  descendantRoutes: Route[];
  routePath: string;
}

export function PrivateRoute(props: PrivateRouteProps) {
  const isAuth = localStorage.getItem('token');
  const location = useLocation();
  const { title, updateScene } = props;

  const { t } = useTranslator();

  // Sort routes by specificity: more specific paths first (longer paths, fewer wildcards)
  const matchedRoute = useMemo(() => {
    const sortedRoutes = props.descendantRoutes?.slice().sort((a, b) => {
      const aPath = a.path;
      const bPath = b.path;

      // Count segments (parts separated by /)
      const aSegments = aPath.split('/').filter(Boolean).length;
      const bSegments = bPath.split('/').filter(Boolean).length;

      // Count wildcards (params starting with :)
      const aWildcards = (aPath.match(/:[^/]+/g) || []).length;
      const bWildcards = (bPath.match(/:[^/]+/g) || []).length;

      // More segments = more specific
      if (aSegments !== bSegments) {
        return bSegments - aSegments;
      }

      // Fewer wildcards = more specific
      return aWildcards - bWildcards;
    });

    return sortedRoutes?.find((descendantRoute) => {
      return matchPath(props.routePath.slice(0, -2) + descendantRoute.path, location.pathname);
    });
  }, [props.descendantRoutes, props.routePath, location.pathname]);

  useEffect(() => {
    // disable title change if disableTitleChangePostMsg (set in descendant routes) is true
    if (!matchedRoute?.disableTitleChangePostMsg) {
      callNativeMobileApp({
        action: 'title_changed',
        payload: {
          title: t(`${matchedRoute ? matchedRoute.title : title}`),
        },
      });
    }

    const lastPlayedSceneString = sessionStorage.getItem('lastPlayedAudioToUpdate');

    if (lastPlayedSceneString && !location.pathname.includes('motivation')) {
      const lastPlayedScene = JSON.parse(lastPlayedSceneString);
      const update = async () => {
        const res = await updateScene(
          lastPlayedScene.sceneId,
          lastPlayedScene.lastPlaybackPosition,
          lastPlayedScene.isCompleted,
          lastPlayedScene.__typename
        );

        if (res.scene.updateScenePlaybackMetadata.code === 200) {
          sessionStorage.removeItem('lastPlayedAudioToUpdate');
        }
      };

      if (lastPlayedScene) {
        update();
      }
    }
  }, [location.pathname, matchedRoute, t, title, updateScene]);

  return isAuth ? (
    <Outlet context={{ brand: 'raize', trackFirebaseEvent: props.trackFirebaseEvent }} />
  ) : (
    <Navigate to="/auth/login" state={{ from: location }} />
  );
}

export default withFirebase(PrivateRoute);
